import joi, { SchemaLike } from "joi";
import { BoolSchema } from "./schema/type/BoolSchema";
import { IntegerSchema } from "./schema/type/IntegerSchema";
import { FloatSchema } from "./schema/type/FloatSchema";
import { StringSchema } from "./schema/type/StringSchema";
import { DateSchema } from "./schema/type/DateSchema";
import { ArraySchema } from "./schema/type/ArraySchema";
import { ObjectSchema } from "./schema/type/ObjectSchema";
import { AnySchema } from "./schema/type/AnySchema";
import { TinyIntSchema } from "./schema/type/TinyIntSchema";
import { SmallIntSchema } from "./schema/type/SmallIntSchema";
import { MediumIntSchema } from "./schema/type/MediumIntSchema";
import { BigIntSchema } from "./schema/type/BigIntSchema";
import { DoubleSchema } from "./schema/type/DoubleSchema";
import { DecimalSchema } from "./schema/type/DecimalSchema";
import { RealSchema } from "./schema/type/RealSchema";
import { DateTimeSchema } from "./schema/type/DateTimeSchema";
import { TimeSchema } from "./schema/type/TimeSchema";
import { DurationSchema } from "./schema/type/DurationSchema";
import { BaseNumberSchema } from "./schema/type/BaseNumberSchema";
import { BasePrecisionSchema } from "./schema/type/BasePrecisionSchema";
import { BaseTypeSchema } from "./schema/BaseTypeSchema";
import { EnumSchema } from "./schema/type/EnumSchema";
import { BaseTypeBuilder } from "./BaseTypeBuilder";
import { EmailSchema } from "./schema/type/EmailSchema";
import { PhoneSchema } from "./schema/type/PhoneSchema";
import { IPV4Schema } from "./schema/type/IPV4Schema";
import { IPV6Schema } from "./schema/type/IPV6Schema";
import { MoneySchema } from "./schema/type/MoneySchema";
import { TimeZoneSchema } from "./schema/type/TimeZoneSchema";
import { VersionSchema } from "./schema/type/VersionSchema";
import { IPV4RangeSchema } from "./schema/type/IPV4RangeSchema";
import { IPV6RangeSchema } from "./schema/type/IPV6RangeSchema";
import { ColorSchema } from "./schema/type/ColorSchema";
import { EntitySchema } from "./schema/type/EntitySchema";
import { URLSchema } from "./schema/type/URLSchema";
import { FilePathSchema } from "./schema/type/FilePathSchema";
import { FontSchema } from "./schema/type/FontSchema";
import { TypeSchema } from "./schema/type/TypeSchema";
import { VariableSchema } from "./schema/type/VariableSchema";

export class JoiBuilder extends BaseTypeBuilder<SchemaLike>
{
    constructor(schema: BaseTypeSchema | null)
    {
        super(schema);
        this.toBase = this.toBase.bind(this);
        this.toLength = this.toLength.bind(this);
        this.toPrecision = this.toPrecision.bind(this);
    }
    private toBase<T extends joi.AnySchema>(schema: BoolSchema, ans: T): T
    {
        if (schema.required)
            ans = ans.required();
        else
            ans = ans.allow(null, "");
        if (schema.valids.length > 0)
            ans = ans.valid(...schema.valids);
        if (schema.invalids.length > 0)
            ans = ans.invalid(...schema.invalids);
        return ans;
    }
    private toLength<T extends joi.NumberSchema>(schema: BaseNumberSchema, ans: T): T
    {
        ans = this.toBase(schema, ans);
        if (schema.min != null)
            ans = ans.min(schema.min);
        if (schema.max != null)
            ans = ans.max(schema.max);
        return ans;
    }
    private toPrecision<T extends joi.NumberSchema>(schema: BasePrecisionSchema, ans: T): T
    {
        ans = this.toLength(schema, ans);
        if (schema.precision != null)
            ans = ans.precision(schema.precision);
        return ans;
    }
    /* VariableType */
    protected override _toAny(schema: AnySchema)
    {
        return this.toBase(schema, joi.any());
    }
    protected override _toBool(schema: BoolSchema)
    {
        return this.toBase(schema, joi.boolean());
    }
    protected override _toTinyInt(schema: TinyIntSchema)
    {
        return this.toLength(schema, joi.number().integer());
    }
    protected override _toSmallInt(schema: SmallIntSchema)
    {
        return this.toLength(schema, joi.number().integer());
    }
    protected override _toMediumInt(schema: MediumIntSchema)
    {
        return this.toLength(schema, joi.number().integer());
    }
    protected override _toInteger(schema: IntegerSchema)
    {
        return this.toLength(schema, joi.number().integer());
    }
    protected override _toBigInt(schema: BigIntSchema)
    {
        return this.toLength(schema, joi.number().integer());
    }
    protected override _toFloat(schema: FloatSchema)
    {
        return this.toPrecision(schema, joi.number());
    }
    protected override _toDouble(schema: DoubleSchema)
    {
        return this.toPrecision(schema, joi.number());
    }
    protected override _toDecimal(schema: DecimalSchema)
    {
        return this.toPrecision(schema, joi.number());
    }
    protected override _toReal(schema: RealSchema)
    {
        return this.toPrecision(schema, joi.number());
    }
    protected override _toMoney(schema: MoneySchema)
    {
        return this.toPrecision(schema, joi.number());
    }
    protected override _toString(schema: StringSchema)
    {
        let ans = this.toBase(schema, joi.string());
        if (schema.min_length != null)
            ans = ans.min(schema.min_length);
        if (schema.max_length != null)
            ans = ans.max(schema.max_length);
        if (schema.regex)
            ans = ans.regex(schema.regex);
        return ans;
    }
    protected override _toDate(schema: DateSchema)
    {
        return this.toBase(schema, joi.date().iso());
    }
    protected override _toTime(schema: TimeSchema)
    {
        return this.toBase(schema, joi.string().regex(TimeSchema.REGEX));
    }
    protected override _toDateTime(schema: DateTimeSchema)
    {
        return this.toBase(schema, joi.date());
    }
    protected override _toTimeZone(schema: TimeZoneSchema)
    {
        return this.toBase(schema, joi.string());
    }
    protected override _toDuration(schema: DurationSchema)
    {
        return this.toBase(schema, joi.string().regex(DurationSchema.REGEX));
    }
    protected override _toEmail(schema: EmailSchema)
    {
        return this.toBase(schema, joi.string().email());
    }
    protected override _toPhone(schema: PhoneSchema)
    {
        return this.toBase(schema, joi.string().regex(PhoneSchema.REGEX));
    }
    protected override _toVersion(schema: VersionSchema)
    {
        return this.toBase(schema, joi.string().regex(VersionSchema.REGEX));
    }
    protected override _toIPV4(schema: IPV4Schema)
    {
        return this.toBase(schema, joi.string().regex(IPV4Schema.REGEX));
    }
    protected override _toIPV6(schema: IPV6Schema)
    {
        return this.toBase(schema, joi.string().regex(IPV6Schema.REGEX));
    }
    protected override _toIPV4Range(schema: IPV4RangeSchema)
    {
        return this.toBase(schema, joi.string().regex(IPV4RangeSchema.REGEX));
    }
    protected override _toIPV6Range(schema: IPV6RangeSchema)
    {
        return this.toBase(schema, joi.string().regex(IPV6RangeSchema.REGEX));
    }
    protected override _toColor(schema: ColorSchema)
    {
        return this.toBase(schema, joi.string());
    }
    protected override _toFont(schema: FontSchema)
    {
        return this.toBase(schema, joi.string());
    }
    protected override _toFilePath(schema: FilePathSchema)
    {
        return this.toBase(schema, joi.string());
    }
    protected override _toURL(schema: URLSchema)
    {
        return this.toBase(schema, joi.string());
    }
    protected override _toEnum(schema: EnumSchema)
    {
        return this.toBase(schema, joi.string());
    }
    protected override _toEntity(schema: EntitySchema)
    {
        return this.toBase(schema, joi.string().regex(schema.getRegex()));
    }
    protected override _toArray(schema: ArraySchema, callback: (schema: BaseTypeSchema) => void)
    {
        let ans = this.toBase(schema, joi.array());
        let ps = schema.items.map(item =>
        {
            let builder = new JoiBuilder(item);
            return builder.runSynch(callback);
        });
        ans = ans.items(...ps);
        if (schema.min != null)
            ans = ans.min(schema.min);
        if (schema.max != null)
            ans = ans.max(schema.max);
        return ans;
    }
    protected override _toObject(schema: ObjectSchema, callback: (schema: BaseTypeSchema) => void)
    {
        if (schema.external != null || schema.fields == null)
            return this.toBase(schema, joi.object());
        let sub: { [name: string]: SchemaLike } = {};
        for (let i = 0; i < schema.fields.length; i++)
        {
            const f = schema.fields[i];
            let builder = new JoiBuilder(f.type);
            sub[f.name] = builder.runSynch(callback);
        }
        return this.toBase(schema, joi.object(sub));
    }
    protected override _toType(schema: TypeSchema): joi.SchemaLike
    {
        return this.toBase(schema, joi.any());
    }
    protected override _toVariable(schema: VariableSchema): joi.SchemaLike
    {
        return this.toBase(schema, joi.any());
    }
}